import type {
  RawProduct,
  RawImage,
  RawDefaultVariation,
  RawVariationGallery,
} from "../../../../../../types";
import type { TransformedProduct } from "../../../types/productApiTypes";

export interface DynamicProductDetails {
  hasDefaultVariation: boolean;
  stockStatus: "instock" | "outofstock" | "onbackorder";
  onBackorder: boolean;
  finalImages: RawImage[];
  sku: string;
  minStock: number;
  maxStock: number;
  lowStockRemaining: number | null;
}

/**
 * A pure helper function to determine core product details that may change
 * based on the presence of a default variation (e.g., stock, SKU, images).
 * This function now handles image logic for parent products, variations, and simple products.
 *
 * @param product The raw product object from the API.
 * @param defaultVariation The raw default variation object, or null.
 * @param variationGalleryData The raw variation gallery data for the product.
 * @returns An object containing the final calculated details.
 */
export const getDynamicProductDetails = (
  product: RawProduct,
  defaultVariation: RawDefaultVariation | null,
  variationGalleryData: RawVariationGallery
): DynamicProductDetails => {
  const hasDefaultVariation = !!defaultVariation;

  let stockStatus: TransformedProduct["stockStatus"] = "outofstock";
  let onBackorder = false;
  let finalImages: RawImage[] = product.images || [];
  let sku = product.sku || "";
  let minStock: number;
  let maxStock: number;
  let lowStockRemaining: number | null = null;

  // --- Image Logic ---
  if (product.type === "variation") {
    // Case 1: The product is a variation itself. Combine its main image with its specific gallery.
    const mainImage = product.images?.[0];
    const galleryImages = variationGalleryData.items || [];
    finalImages = mainImage ? [mainImage, ...galleryImages] : galleryImages;
  } else if (hasDefaultVariation) {
    // Case 2: The product is a parent with a default variation. Prepend the variation's image.
    const defaultImage = defaultVariation.image;
    if (defaultImage) {
      finalImages = [
        defaultImage,
        ...finalImages.filter((img) => img.id !== defaultImage.id),
      ];
    }
  }
  // Case 3 (else): A simple product or a variable product without a default variation.
  // The initial `finalImages = product.images || []` is already correct.

  // --- Stock, SKU, and Stock Limits Logic ---
  if (hasDefaultVariation) {
    // For parent products with a default variation
    stockStatus =
      defaultVariation.stock_status as TransformedProduct["stockStatus"];

    // Now reading from the new field provided by the backend extender
    onBackorder = defaultVariation.is_on_backorder;
    lowStockRemaining = defaultVariation.low_stock_remaining;

    // Calculate onBackorder based on stock status string for default variation,
    // as the extension object might not have the boolean property directly.
    onBackorder = stockStatus === "onbackorder";

    sku = defaultVariation.sku;
    minStock = defaultVariation.min_stock;
    maxStock = defaultVariation.max_stock;

    // The default variation object in the extension might not carry low_stock_remaining
    // depending on API version/customization. Safest to default to null or check if it exists.
    // lowStockRemaining = defaultVariation.low_stock_remaining ?? null;
    // (Assuming standard Store API extension schema doesn't have it yet, keeping null).
    lowStockRemaining = null;
  } else {
    // For simple products OR individual variations (which don't have a defaultVariation)
    if (product.is_in_stock) {
      stockStatus = product.is_on_backorder ? "onbackorder" : "instock";
    }
    // Use explicit boolean from API if available
    onBackorder = product.is_on_backorder;

    // Map the low stock value directly from the raw product response
    lowStockRemaining = product.low_stock_remaining;

    // SKU is already correctly set from the initial `let sku = product.sku || ""`
    minStock = product.add_to_cart.minimum;
    maxStock = product.add_to_cart.maximum;
  }

  return {
    hasDefaultVariation,
    stockStatus,
    onBackorder,
    finalImages,
    sku,
    minStock,
    maxStock,
    lowStockRemaining,
  };
};
